import type { Contact } from "@/components/atomic-crm/types";
import type { Ticket } from "@/modules/types";
import { getTicketClientFirstName } from "@/modules/tickets/ticketRequester";
import {
  buildFileDeliveryEmail,
  buildPaymentRequestEmail,
} from "../../../supabase/functions/_shared/billingEmails.ts";
import { workspaceEmailBrand } from "@/lib/workspaceBranding";

export const DEFAULT_TICKET_PAYMENT_EMAIL_MESSAGE =
  "Your project deliverables are ready.\n\nPlease pay using the secure link below. After payment you will receive one email with your files attached.";

export const DEFAULT_TICKET_PAYMENT_REMINDER_MESSAGE =
  "This is a friendly reminder that your invoice is still unpaid.\n\nPlease pay using the secure link below to complete payment. Your files will be delivered automatically after payment.";

export const resolveTicketPropertyAddress = (ticket: Pick<Ticket, "subject">) =>
  ticket.subject?.trim() || "Your property";

/** @deprecated Prefer buildTicketPaymentCopyFromDeliverables */
export const buildTicketPaymentEmailSubject = (propertyAddress: string) =>
  `Invoice for services (${propertyAddress.trim()})`;

/** @deprecated Prefer buildTicketPaymentReminderCopyFromDeliverables */
export const buildTicketPaymentReminderSubject = (propertyAddress: string) =>
  `Reminder: Invoice for services (${propertyAddress.trim()})`;

/** @deprecated Prefer buildTicketPaymentCopyFromDeliverables */
export const buildTicketDeliveryEmailSubject = (propertyAddress: string) =>
  `Your project files are ready (${propertyAddress.trim()})`;

/** Same HTML the edge function sends (shared billing email layout). */
export const buildTicketPaymentEmailHtml = (params: {
  orgName: string;
  invoiceNumber: string;
  amountFormatted: string;
  paymentUrl: string;
  customMessage?: string;
  serviceLines?: string[];
  dueDate?: string | null;
}) =>
  buildPaymentRequestEmail({
    brand: workspaceEmailBrand(params.orgName),
    invoiceNumber: params.invoiceNumber,
    amountFormatted: params.amountFormatted,
    dueDate: params.dueDate,
    paymentUrl: params.paymentUrl,
    message:
      params.customMessage?.trim() || DEFAULT_TICKET_PAYMENT_EMAIL_MESSAGE,
    serviceLines: params.serviceLines,
  }).html;

export {
  buildTicketPaymentReminderSmsText,
  buildTicketPaymentSmsText,
} from "@/modules/tickets/ticketInvoiceCopy";

export const buildTicketDeliveryEmailHtml = (params: {
  orgName: string;
  invoiceNumber: string;
  propertyAddress: string;
  fileNames: string[];
  portalUrl?: string;
}) =>
  buildFileDeliveryEmail({
    brand: workspaceEmailBrand(params.orgName),
    invoiceNumber: params.invoiceNumber,
    propertyAddress: params.propertyAddress,
    portalUrl: params.portalUrl ?? "https://www.nomicrm.com/p/preview",
    files: params.fileNames.map((name) => ({ name, attached: true })),
  }).html;

export const buildTicketDeliveryPreviewText = (params: {
  ticket: Ticket;
  contact?: Contact | null;
  invoiceNumber: string;
  fileNames: string[];
}) => {
  const firstName = getTicketClientFirstName(params.ticket, params.contact);
  const propertyAddress = resolveTicketPropertyAddress(params.ticket);
  const fileList =
    params.fileNames.length > 0
      ? params.fileNames.join(", ")
      : "your supplement files";

  return `Hi ${firstName},\n\nThank you for your payment (${params.invoiceNumber}). Your supplement files for ${propertyAddress} are attached: ${fileList}.`;
};

export const buildTicketDeliverySmsText = (params: {
  orgName: string;
  ticket: Ticket;
  contact?: Contact | null;
  invoiceNumber: string;
  fileNames: string[];
}) => {
  const firstName = getTicketClientFirstName(params.ticket, params.contact);
  const propertyAddress = resolveTicketPropertyAddress(params.ticket);
  const fileList =
    params.fileNames.length > 0
      ? params.fileNames.join(", ")
      : "your supplement files";

  return [
    `${params.orgName} here:`,
    firstName ? `Hi ${firstName},` : "Hi,",
    `Thank you for your payment (${params.invoiceNumber}).`,
    `Your supplement files for ${propertyAddress} are ready: ${fileList}.`,
  ].join("\n");
};

export const buildTicketPaymentThankYouSmsText = (params: {
  orgName: string;
  ticket: Ticket;
  contact?: Contact | null;
  invoiceNumber: string;
  amountFormatted: string;
}) => {
  const firstName = getTicketClientFirstName(params.ticket, params.contact);
  const propertyAddress = resolveTicketPropertyAddress(params.ticket);

  return [
    `${params.orgName} here:`,
    firstName ? `Hi ${firstName},` : "Hi,",
    `We received your payment of ${params.amountFormatted} for invoice ${params.invoiceNumber}.`,
    `Your supplement files for ${propertyAddress} will follow shortly.`,
  ].join("\n");
};
